import { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react'
import type { ReactNode } from 'react'
import type { GovModule } from './governanceCards'
import GovernanceDurableBridge from './GovernanceDurableBridge'
import {
  buildInitialState, governanceReducer,
  can as canSelector, effectiveRole, getModuleHealth, getModuleMetrics,
  getRecord, getRecordsByCorrelationId, recordsByModule,
  type GovState, type GovCommand, type GovCommandInput,
  type GovAction, type PermissionDecision, type ModuleMetrics, type ModuleHealth,
  type GovRecord, type GovRole,
} from './governanceModel'

export interface GovernanceContextValue {
  state: GovState
  /** Dispatch a command; the current wall-clock `now` is injected automatically. */
  run: (cmd: GovCommandInput) => void
  can: (action: GovAction) => PermissionDecision
  role: GovRole
  metrics: (module: GovModule) => ModuleMetrics
  health: (module: GovModule) => ModuleHealth
  byModule: (module: GovModule) => GovRecord[]
  record: (id: string) => GovRecord | undefined
  correlated: (correlationId: string) => GovRecord[]
}

const GovernanceContext = createContext<GovernanceContextValue | null>(null)

export interface GovernanceProviderProps {
  children: ReactNode
  /** Injectable clock for deterministic tests. Defaults to Date.now. */
  nowFn?: () => number
  /** Optional starting state (tests / hydration). */
  initialState?: GovState
  /** sessionStorage key for local-session persistence. Set to null to disable. */
  storageKey?: string | null
  /** Mount the durable-source bridge (real olv_audit_trail / chain reads). */
  enableDurable?: boolean
}

const DEFAULT_STORAGE_KEY = 'olv_gov_state_v1'

/** SSR-safe read of persisted local-session state (browser only). */
function readPersisted(storageKey: string | null): GovState | null {
  if (!storageKey || typeof window === 'undefined' || !window.sessionStorage) return null
  try {
    const raw = window.sessionStorage.getItem(storageKey)
    if (!raw) return null
    const parsed = JSON.parse(raw) as GovState
    if (!parsed || !Array.isArray(parsed.records) || typeof parsed.seq !== 'number') return null
    return parsed
  } catch {
    return null
  }
}

/**
 * GovernanceProvider — the single in-memory source of truth for the Olivium
 * governance suite. Wraps the pure reducer, injects the wall-clock at dispatch
 * time, and exposes selectors. SSR-safe (useReducer renders on the server).
 *
 * Durable hydration (real olv_audit_trail / receipts / sq_access_codes reads)
 * is layered on top in commit_4 without changing this contract.
 */
export function GovernanceProvider({ children, nowFn, initialState, storageKey = DEFAULT_STORAGE_KEY, enableDurable = true }: GovernanceProviderProps) {
  const [state, dispatch] = useReducer(
    governanceReducer,
    initialState,
    (init) => init ?? readPersisted(storageKey) ?? buildInitialState(),
  )

  const run = useCallback((cmd: GovCommandInput) => {
    const now = nowFn ? nowFn() : Date.now()
    dispatch({ ...cmd, now } as GovCommand)
  }, [nowFn])

  // Persist the local-session governance ledger so edits survive navigation
  // between the module list and record routes. Browser-only; a no-op in SSR.
  useEffect(() => {
    if (!storageKey || typeof window === 'undefined' || !window.sessionStorage) return
    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify(state))
    } catch {
      /* storage full / disabled — the in-memory ledger still works */
    }
  }, [state, storageKey])

  const value = useMemo<GovernanceContextValue>(() => ({
    state,
    run,
    can: (action) => canSelector(state, action),
    role: effectiveRole(state),
    metrics: (module) => getModuleMetrics(state, module),
    health: (module) => getModuleHealth(state, module),
    byModule: (module) => recordsByModule(state, module),
    record: (id) => getRecord(state, id),
    correlated: (correlationId) => getRecordsByCorrelationId(state, correlationId),
  }), [state, run])

  return (
    <GovernanceContext.Provider value={value}>
      {enableDurable ? <GovernanceDurableBridge /> : null}
      {children}
    </GovernanceContext.Provider>
  )
}

export function useGovernance(): GovernanceContextValue {
  const ctx = useContext(GovernanceContext)
  if (!ctx) throw new Error('useGovernance must be used within a GovernanceProvider')
  return ctx
}

/** Provider-optional variant: returns null outside a provider (SSR / shell reuse). */
export function useGovernanceOptional(): GovernanceContextValue | null {
  return useContext(GovernanceContext)
}
